import {gql} from '@apollo/client';

import {
  MonomerCapGroup,
  MonomerMonomerType,
  MonomerNaturalAnalogue,
  MonomerVisualSymbol,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import FragmentWrapper from 'graphql-client/FragmentWrapper';
import staticURL from 'lib/staticURL';

import {MonomerGraphQL} from './__generated/MonomerGraphQL';

// Keep in sync with benchling.molbio.sequences.modified_nucleotides.modified_nucleotide_constants
export const DEGENERATE_BASE = 'DEGENERATE_BASE';

export const MONOMER_FRAGMENT = gql`
  fragment MonomerGraphQL on MonomerNode {
    id
    name
    symbol
    naturalAnalogue
    polymerType
    monomerType
    molfile
    canonicalSmiles
    originalSmiles
    attachmentPoints
    isNatural
    color
    visualSymbol
    chemicalStructureImage {
      ... on SVGNode {
        base64Svg
      }
      ... on ErrorNode {
        message
      }
    }
    calculatedMolecularWeight
    customMolecularWeight
    calculatedExactMolecularWeight
    ...HasArchiveRecordGraphQL
  }
  ${getFragmentWrapper('HasArchiveRecord').fragment}
`;

export const MONOMER_QUERY = gql`
  query MonomerQuery @skipAudit @engComponent(name: MODIFIED_BIOLOGICS) {
    monomers {
      id
      name
      symbol
      naturalAnalogue
      polymerType
      monomerType
      molfile
      canonicalSmiles
      originalSmiles
      attachmentPoints
      isNatural
      color
      visualSymbol
      chemicalStructureImage {
        ... on SVGNode {
          base64Svg
        }
        ... on ErrorNode {
          message
        }
      }
      calculatedMolecularWeight
      calculatedExactMolecularWeight
      customMolecularWeight
      ...HasArchiveRecordGraphQL
    }
  }
  ${getFragmentWrapper('HasArchiveRecord').fragment}
`;

export const MonomerFragment: FragmentWrapper<MonomerGraphQL> = {
  fragmentName: 'MonomerGraphQL',
  fragment: MONOMER_FRAGMENT,
};

export const SINGLE_MONOMER_QUERY = gql`
  query SingleMonomerQuery($id: StrictString!) @skipAudit @engComponent(name: MODIFIED_BIOLOGICS) {
    monomer(id: $id) {
      ...MonomerGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${MonomerFragment.fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export enum MONOMER_TYPE_DISPLAY_NAME {
  BACKBONE = 'Backbone',
  BRANCH = 'Branch',
}

export enum MONOMER_ANALOGUE_DISPLAY_NAME {
  A = 'A',
  C = 'C',
  G = 'G',
  N = 'N',
  P_LOWER = 'p',
  R_LOWER = 'r',
  D_LOWER = 'd',
  T = 'T',
  U = 'U',
  R = 'R',
  Y = 'Y',
  M = 'M',
  K = 'K',
  W = 'W',
  S = 'S',
  B = 'B',
  D = 'D',
  H = 'H',
  V = 'V',
}

export const MONOMER_ANALOGUE_DESCRIPTOR: {[key in MonomerNaturalAnalogue]: string} = {
  [MonomerNaturalAnalogue.A]: 'Adenine',
  [MonomerNaturalAnalogue.C]: 'Cytosine',
  [MonomerNaturalAnalogue.G]: 'Guanine',
  [MonomerNaturalAnalogue.N]: 'Any',
  [MonomerNaturalAnalogue.P_LOWER]: 'Phosphate',
  [MonomerNaturalAnalogue.R_LOWER]: 'Ribose',
  [MonomerNaturalAnalogue.D_LOWER]: 'Deoxyribose',
  [MonomerNaturalAnalogue.T]: 'Thymine',
  [MonomerNaturalAnalogue.U]: 'Uracil',
  [MonomerNaturalAnalogue.R]: 'Purine',
  [MonomerNaturalAnalogue.Y]: 'Pyrimidine',
  [MonomerNaturalAnalogue.M]: 'Amino',
  [MonomerNaturalAnalogue.K]: 'Keto',
  [MonomerNaturalAnalogue.W]: 'Weak',
  [MonomerNaturalAnalogue.S]: 'Strong',
  [MonomerNaturalAnalogue.B]: 'Not Adenine',
  [MonomerNaturalAnalogue.D]: 'Not Cytosine',
  [MonomerNaturalAnalogue.H]: 'Not Guanine',
  [MonomerNaturalAnalogue.V]: 'Not Thymine',
};

export const MONOMER_ANALOGUE_COMPATIBILITY: {[key in MonomerNaturalAnalogue]: string} = {
  [MonomerNaturalAnalogue.A]: '(A)',
  [MonomerNaturalAnalogue.C]: '(C)',
  [MonomerNaturalAnalogue.G]: '(G)',
  [MonomerNaturalAnalogue.N]: '(A/C/G/T/U)',
  [MonomerNaturalAnalogue.P_LOWER]: '(p)',
  [MonomerNaturalAnalogue.R_LOWER]: '(r)',
  [MonomerNaturalAnalogue.D_LOWER]: '(d)',
  [MonomerNaturalAnalogue.T]: '(T)',
  [MonomerNaturalAnalogue.U]: '(U)',
  [MonomerNaturalAnalogue.R]: '(A/G)',
  [MonomerNaturalAnalogue.Y]: '(C/T/U)',
  [MonomerNaturalAnalogue.M]: '(A/C)',
  [MonomerNaturalAnalogue.K]: '(G/T/U)',
  [MonomerNaturalAnalogue.W]: '(A/T/U)',
  [MonomerNaturalAnalogue.S]: '(C/G)',
  [MonomerNaturalAnalogue.B]: '(C/G/T/U)',
  [MonomerNaturalAnalogue.D]: '(A/G/T/U)',
  [MonomerNaturalAnalogue.H]: '(A/C/T/U)',
  [MonomerNaturalAnalogue.V]: '(A/C/G)',
};

export enum NucleotideComponent {
  BASE = 'base',
  PHOSPHATE = 'phosphate',
  SUGAR = 'sugar',
}

export const DEFAULT_MODIFIED_BASE_COLOR = '#7051FC33';
export const UNMODIFIED_BASE_COLOR = '#5C7C83';
export const DEFAULT_MODIFIED_SUGAR_COLOR = '#7051FC';

export const NUCLEOTIDES_TABLE_DEFAULT_PAGE_SIZE = 25;
export enum ModifiedNucleotidesPreferences {
  NUCLEOTIDES_TABLE_PAGE_SIZE = 'NUCLEOTIDES_TABLE_PAGE_SIZE',
  HAS_DISMISSED_UT_FLEXIBILITY_BANNER = 'HAS_DISMISSED_UT_FLEXIBILITY_BANNER',
}

export enum MissingMonomerReason {
  CHOOSING = 'CHOOSING',
  DEGENERATE = 'DEGENERATE',
  NULL_MONOMER = 'NULL_MONOMER',
}

export const HollowMonomerVisualSymbols = [
  MonomerVisualSymbol.CLOVER_HOLLOW,
  MonomerVisualSymbol.DIAMOND_HOLLOW,
  MonomerVisualSymbol.DYAD_HOLLOW,
  MonomerVisualSymbol.RECTANGLE_HOLLOW,
  MonomerVisualSymbol.STAR_HOLLOW,
  MonomerVisualSymbol.TRIAD_HOLLOW,
  MonomerVisualSymbol.TRIANGLE_HOLLOW,
];

export const NaturalAnalogueToNucleotideComponent = {
  [MonomerNaturalAnalogue.A]: NucleotideComponent.BASE,
  [MonomerNaturalAnalogue.C]: NucleotideComponent.BASE,
  [MonomerNaturalAnalogue.D_LOWER]: NucleotideComponent.SUGAR,
  [MonomerNaturalAnalogue.G]: NucleotideComponent.BASE,
  [MonomerNaturalAnalogue.N]: NucleotideComponent.BASE,
  [MonomerNaturalAnalogue.P_LOWER]: NucleotideComponent.PHOSPHATE,
  [MonomerNaturalAnalogue.R_LOWER]: NucleotideComponent.SUGAR,
  [MonomerNaturalAnalogue.T]: NucleotideComponent.BASE,
  [MonomerNaturalAnalogue.U]: NucleotideComponent.BASE,
  [MonomerNaturalAnalogue.R]: NucleotideComponent.BASE,
  [MonomerNaturalAnalogue.Y]: NucleotideComponent.BASE,
  [MonomerNaturalAnalogue.M]: NucleotideComponent.BASE,
  [MonomerNaturalAnalogue.K]: NucleotideComponent.BASE,
  [MonomerNaturalAnalogue.W]: NucleotideComponent.BASE,
  [MonomerNaturalAnalogue.S]: NucleotideComponent.BASE,
  [MonomerNaturalAnalogue.B]: NucleotideComponent.BASE,
  [MonomerNaturalAnalogue.D]: NucleotideComponent.BASE,
  [MonomerNaturalAnalogue.H]: NucleotideComponent.BASE,
  [MonomerNaturalAnalogue.V]: NucleotideComponent.BASE,
};

export const RECOMMENDED_CUSTOM_BASE_COLORS = [
  DEFAULT_MODIFIED_BASE_COLOR,
  '#FC9F354D',
  '#2669FF40',
  '#C7C9D0',
  '#0BCAB54D',
  '#CF09EF40',
  '#FF475A4D',
  '#FDEE94',
  '#C3FB7D',
  '#C0E5D8',
  '#98D8E7',
  '#8CB0D8',
  '#CD998AB3',
];

export const RECOMMENDED_CUSTOM_SUGAR_COLORS = [
  DEFAULT_MODIFIED_SUGAR_COLOR,
  '#FC9F35',
  '#2669FF',
  '#344043',
  '#0BCAB5',
  '#CF09EF',
  '#FF475A',
  '#FDEE94',
  '#F3D3AE',
  '#C3FB7D',
  '#98D8E7',
  '#A0AFCF',
  '#CD998A',
  '#B2ABAC',
];

export const RECOMMENDED_CUSTOM_VISUAL_SYMBOLS = [
  MonomerVisualSymbol.DIAMOND_FILLED,
  MonomerVisualSymbol.DIAMOND_HOLLOW,
  MonomerVisualSymbol.CLOVER_HOLLOW,
  MonomerVisualSymbol.CLOVER_FILLED,
  MonomerVisualSymbol.RECTANGLE_FILLED,
  MonomerVisualSymbol.RECTANGLE_HOLLOW,
  MonomerVisualSymbol.TRIANGLE_FILLED,
  MonomerVisualSymbol.TRIANGLE_HOLLOW,
  MonomerVisualSymbol.STAR_FILLED,
  MonomerVisualSymbol.STAR_HOLLOW,
  MonomerVisualSymbol.LETTERS_C,
  MonomerVisualSymbol.LETTERS_CO,
  MonomerVisualSymbol.LETTERS_M,
  MonomerVisualSymbol.LETTERS_MP,
  MonomerVisualSymbol.LETTERS_P,
  MonomerVisualSymbol.LETTERS_PS,
  MonomerVisualSymbol.LETTERS_R,
  MonomerVisualSymbol.LETTERS_RS,
  MonomerVisualSymbol.LETTERS_S,
  MonomerVisualSymbol.LETTERS_SP,
];

export enum MONOMER_VISUAL_SYMBOL_DISPLAY_NAME {
  DIAMOND_FILLED = 'Diamond (filled)',
  DIAMOND_HOLLOW = 'Diamond (hollow',
  DIAMOND_DASHED = 'Diamond (dashed)',
  CLOVER_FILLED = 'Clover (filled)',
  CLOVER_HOLLOW = 'Clover (hollow)',
  RECTANGLE_FILLED = 'Rectangle (filled)',
  RECTANGLE_HOLLOW = 'Rectangle (hollow)',
  TRIANGLE_FILLED = 'Triangle (filled)',
  TRIANGLE_HOLLOW = 'Triangle (hollow)',
  LETTERS_P = 'Letters (P)',
  LETTERS_PS = 'Letters (Ps)',
  STAR_FILLED = 'Star (filled)',
  STAR_HOLLOW = 'Star (hollow)',
  TRIAD_FILLED = 'Triad (filled)',
  TRIAD_HOLLOW = 'Triad (hollow)',
  DYAD_FILLED = 'Dyad (filled)',
  DYAD_HOLLOW = 'Dyad (hollow)',
  LETTERS_C = 'Letters (C)',
  LETTERS_CO = 'Letters (Co)',
  LETTERS_M = 'Letters (M)',
  LETTERS_MP = 'Letters (Mp)',
  LETTERS_R = 'Letters(R)',
  LETTERS_RS = 'Letters(Rs)',
  LETTERS_S = 'Letters (S)',
  LETTERS_SP = 'Letters (Sp)',
}

export const ORDERED_ANALOGUES = [
  MonomerNaturalAnalogue.A,
  MonomerNaturalAnalogue.C,
  MonomerNaturalAnalogue.G,
  MonomerNaturalAnalogue.T,
  MonomerNaturalAnalogue.U,
  MonomerNaturalAnalogue.R_LOWER,
  MonomerNaturalAnalogue.D_LOWER,
  MonomerNaturalAnalogue.P_LOWER,
];

export const ORDERED_DEGENERATE_ANALOGUES = [
  MonomerNaturalAnalogue.N,
  MonomerNaturalAnalogue.R,
  MonomerNaturalAnalogue.Y,
  MonomerNaturalAnalogue.M,
  MonomerNaturalAnalogue.K,
  MonomerNaturalAnalogue.W,
  MonomerNaturalAnalogue.S,
  MonomerNaturalAnalogue.B,
  MonomerNaturalAnalogue.D,
  MonomerNaturalAnalogue.H,
  MonomerNaturalAnalogue.V,
];

export const NATURAL_ANALOGUE_TO_MONOMER_TYPE = {
  [MonomerNaturalAnalogue.R_LOWER]: MonomerMonomerType.BACKBONE,
  [MonomerNaturalAnalogue.D_LOWER]: MonomerMonomerType.BACKBONE,
  [MonomerNaturalAnalogue.P_LOWER]: MonomerMonomerType.BACKBONE,
  [MonomerNaturalAnalogue.A]: MonomerMonomerType.BRANCH,
  [MonomerNaturalAnalogue.C]: MonomerMonomerType.BRANCH,
  [MonomerNaturalAnalogue.G]: MonomerMonomerType.BRANCH,
  [MonomerNaturalAnalogue.T]: MonomerMonomerType.BRANCH,
  [MonomerNaturalAnalogue.U]: MonomerMonomerType.BRANCH,
  [MonomerNaturalAnalogue.N]: MonomerMonomerType.BRANCH,
  [MonomerNaturalAnalogue.R]: MonomerMonomerType.BRANCH,
  [MonomerNaturalAnalogue.Y]: MonomerMonomerType.BRANCH,
  [MonomerNaturalAnalogue.M]: MonomerMonomerType.BRANCH,
  [MonomerNaturalAnalogue.K]: MonomerMonomerType.BRANCH,
  [MonomerNaturalAnalogue.W]: MonomerMonomerType.BRANCH,
  [MonomerNaturalAnalogue.S]: MonomerMonomerType.BRANCH,
  [MonomerNaturalAnalogue.B]: MonomerMonomerType.BRANCH,
  [MonomerNaturalAnalogue.D]: MonomerMonomerType.BRANCH,
  [MonomerNaturalAnalogue.H]: MonomerMonomerType.BRANCH,
  [MonomerNaturalAnalogue.V]: MonomerMonomerType.BRANCH,
};

export const WILDCARD_CAP_GROUP_SYMBOL = '*';

export type MonomerCapGroupWithWildcard = MonomerCapGroup | typeof WILDCARD_CAP_GROUP_SYMBOL;

// Keep in sync with benchling/molbio/monomers/constants.py
export const MISSING_CAPPING_GROUPS_MESSAGE = 'Capping groups must be specified for all attachment points';

export const TEMPLATE_BULK_MONOMERS_CSV_LINK = staticURL('/static/monomers/template_bulk_monomers.csv');
